import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Camera, LogOut, Trash2, Shield, ShieldAlert, Check, X } from 'lucide-react';

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ProfileSettings = ({
  isRTL,
  user,
  token,
  API,
  language,
  setLanguage,
  onBack,
  onUserUpdated,
  onLogout,
  onOpenBlocked,
  onOpenAdmin,
  t,
}) => {
  const [name, setName] = useState(user.name || '');
  const [nickname, setNickname] = useState(user.nickname || '');
  const [bio, setBio] = useState(user.bio || '');
  const [privacy, setPrivacy] = useState(user.profile_privacy || 'public');
  const [pic, setPic] = useState(user.profile_picture_url || null);

  // Phase 18A — live nickname availability check
  const [nickCheck, setNickCheck] = useState({ state: 'idle', code: null });
  useEffect(() => {
    const trimmed = (nickname || '').trim();
    if (!trimmed) {
      setNickCheck({ state: 'idle', code: null });
      return;
    }
    setNickCheck((s) => (s.state === 'checking' ? s : { state: 'checking', code: null }));
    const ctl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/mentions/check`, {
          params: { nickname: trimmed },
          headers,
          signal: ctl.signal,
        });
        if (res.data.available) {
          setNickCheck({ state: 'available', code: null });
        } else {
          setNickCheck({ state: 'unavailable', code: res.data.code || 'invalid' });
        }
      } catch (err) {
        if (axios.isCancel?.(err)) return;
        setNickCheck({ state: 'unavailable', code: 'error' });
      }
    }, 350);
    return () => { clearTimeout(timer); ctl.abort(); };
  }, [nickname, API, token]);

  const nicknameInvalid = nickCheck.state === 'unavailable';
  const nicknameMessage = (() => {
    if (!nickname?.trim()) return null;
    if (nickCheck.state === 'checking') return { tone: 'gray', text: t('checking') };
    if (nickCheck.state === 'available') return { tone: 'emerald', text: t('nicknameAvailable') };
    if (nickCheck.state === 'unavailable') {
      const code = nickCheck.code;
      const map = {
        taken: t('nicknameTaken'),
        too_short: t('nicknameTooShort'),
        too_long: t('nicknameTooLong'),
        reserved: t('nicknameReserved'),
        invalid_chars: t('nicknameInvalidChars'),
        bad_boundary: t('nicknameBadBoundary'),
        double_sep: t('nicknameDoubleSep'),
        all_digits: t('nicknameAllDigits'),
        empty: t('nicknameInvalid'),
        error: t('error'),
      };
      return { tone: 'red', text: map[code] || t('nicknameInvalid') };
    }
    return null;
  })();

  const [savingProfile, setSavingProfile] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [picBusy, setPicBusy] = useState(false);

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);

  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const flashSaved = () => {
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const saveProfile = async (e) => {
    e?.preventDefault?.();
    if (nicknameInvalid) return;  // Phase 18A: block save on invalid/taken
    setSavingProfile(true);
    try {
      const body = {
        name,
        nickname: nickname || null,
        bio: bio || '',
        profile_privacy: privacy,
        language,
      };
      const res = await axios.put(`${API}/auth/profile`, body, { headers });
      onUserUpdated && onUserUpdated(res.data);
      flashSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'taken') {
        setNickCheck({ state: 'unavailable', code: 'taken' });
      }
      const msg = typeof detail === 'string'
        ? detail
        : (detail && detail.message) || t('error');
      alert(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      alert(t('mediaRequired'));
      return;
    }
    setPicBusy(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      const res = await axios.post(
        `${API}/auth/profile/picture`,
        { picture_url: dataUrl },
        { headers }
      );
      const newUrl = res.data?.picture_url || dataUrl;
      setPic(newUrl);
      onUserUpdated && onUserUpdated({ ...user, profile_picture_url: newUrl });
      flashSaved();
    } catch {
      alert(t('error'));
    } finally {
      setPicBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removePicture = async () => {
    setPicBusy(true);
    try {
      await axios.delete(`${API}/auth/profile/picture`, { headers });
      setPic(null);
      onUserUpdated && onUserUpdated({ ...user, profile_picture_url: null });
      flashSaved();
    } catch {
      alert(t('error'));
    } finally {
      setPicBusy(false);
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (!currentPwd || !newPwd) return;
    setPwdBusy(true);
    setPwdMsg(null);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPwd, new_password: newPwd },
        { headers }
      );
      setCurrentPwd('');
      setNewPwd('');
      setPwdMsg({ ok: true, text: t('saved') });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setPwdMsg({ ok: false, text: typeof d === 'string' ? d : t('error') });
    } finally {
      setPwdBusy(false);
    }
  };

  return (
    <div data-testid="settings-screen" dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="settings-back"
            className="flex items-center gap-1 text-purple-600 font-medium"
          >
            <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            <span className="text-sm">{t('back')}</span>
          </button>
          <h1 className="text-base font-bold text-gray-900">{t('settings')}</h1>
          <div className="w-12 text-end">
            {savedFlash && (
              <span data-testid="settings-saved-flash" className="text-xs text-emerald-600 font-medium">
                {t('saved')}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-5 pb-24 space-y-5">
        {/* Avatar */}
        <section className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4">
          {pic ? (
            <img src={pic} alt="avatar" className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white text-3xl font-bold">
              {(name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 flex flex-col gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={picBusy}
              data-testid="settings-change-pic"
              className="inline-flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
            >
              <Camera className="w-4 h-4" />
              {picBusy ? t('saving') : t('changePicture')}
            </button>
            {pic && (
              <button
                onClick={removePicture}
                disabled={picBusy}
                data-testid="settings-remove-pic"
                className="inline-flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                {t('removePicture')}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickFile}
              data-testid="settings-pic-input"
            />
          </div>
        </section>

        {/* Editable fields */}
        <form
          onSubmit={saveProfile}
          className="bg-white rounded-xl border border-gray-100 p-5 space-y-3"
        >
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t('name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="settings-name"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t('nickname')}</label>
            <div className="relative">
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                data-testid="settings-nickname"
                placeholder="username"
                aria-invalid={nicknameInvalid}
                className={`w-full px-3 py-2 pe-9 rounded-lg border focus:outline-none ${
                  nicknameInvalid
                    ? 'border-red-300 focus:border-red-400'
                    : nickCheck.state === 'available'
                    ? 'border-emerald-300 focus:border-emerald-400'
                    : 'border-gray-200 focus:border-purple-400'
                }`}
              />
              {nickCheck.state === 'available' && (
                <Check className="absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
              )}
              {nicknameInvalid && (
                <X className="absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
              )}
            </div>
            {nicknameMessage && (
              <p
                data-testid="settings-nickname-status"
                className={`mt-1 text-[11px] ${
                  nicknameMessage.tone === 'emerald'
                    ? 'text-emerald-600'
                    : nicknameMessage.tone === 'red'
                    ? 'text-red-600'
                    : 'text-gray-500'
                }`}
              >
                {nicknameMessage.text}
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t('bio')}</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              maxLength={300}
              data-testid="settings-bio"
              placeholder={t('bioPlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400"
            />
            <p className="text-[11px] text-gray-400 mt-1 text-end">{bio.length}/300</p>
          </div>

          {/* Privacy */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t('privacy')}</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPrivacy('public')}
                data-testid="settings-privacy-public"
                className={`flex-1 py-2 rounded-lg border text-sm font-medium ${
                  privacy === 'public'
                    ? 'border-purple-400 bg-purple-50 text-purple-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                {t('privacyPublic')}
              </button>
              <button
                type="button"
                onClick={() => setPrivacy('private')}
                data-testid="settings-privacy-private"
                className={`flex-1 py-2 rounded-lg border text-sm font-medium ${
                  privacy === 'private'
                    ? 'border-purple-400 bg-purple-50 text-purple-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                {t('privacyPrivate')}
              </button>
            </div>
          </div>

          {/* Language */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t('languageLabel')}</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setLanguage('ar')}
                data-testid="settings-lang-ar"
                className={`flex-1 py-2 rounded-lg border text-sm font-medium ${
                  language === 'ar'
                    ? 'border-purple-400 bg-purple-50 text-purple-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                {t('arabic')}
              </button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                data-testid="settings-lang-en"
                className={`flex-1 py-2 rounded-lg border text-sm font-medium ${
                  language === 'en'
                    ? 'border-purple-400 bg-purple-50 text-purple-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                {t('english')}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={savingProfile || nicknameInvalid}
            data-testid="settings-save"
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold hover:opacity-95 disabled:opacity-50 transition-opacity"
          >
            {savingProfile ? t('saving') : t('save')}
          </button>
        </form>

        {/* Change password */}
        <form
          onSubmit={submitPasswordChange}
          className="bg-white rounded-xl border border-gray-100 p-5 space-y-3"
        >
          <h3 className="text-sm font-bold text-gray-900">{t('changePassword')}</h3>
          <input
            type="password"
            value={currentPwd}
            onChange={(e) => setCurrentPwd(e.target.value)}
            placeholder={t('currentPassword')}
            data-testid="settings-current-password"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400"
          />
          <input
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            placeholder={t('newPassword')}
            minLength={6}
            data-testid="settings-new-password"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400"
          />
          {pwdMsg && (
            <p
              data-testid="settings-password-msg"
              className={`text-sm ${pwdMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}
            >
              {pwdMsg.text}
            </p>
          )}
          <button
            type="submit"
            disabled={pwdBusy || !currentPwd || !newPwd}
            data-testid="settings-change-password-submit"
            className="w-full py-2.5 rounded-lg border border-gray-200 text-gray-800 font-semibold hover:bg-gray-50 disabled:opacity-50"
          >
            {pwdBusy ? t('saving') : t('update')}
          </button>
        </form>

        {/* Safety - Blocked users */}
        <button
          onClick={onOpenBlocked}
          data-testid="settings-blocked-users"
          className="w-full bg-white rounded-xl border border-gray-100 px-4 py-3.5 flex items-center justify-between hover:bg-gray-50"
        >
          <span className="flex items-center gap-3 text-sm font-semibold text-gray-800">
            <Shield className="w-4 h-4 text-purple-600" />
            {t('blockedUsers')}
          </span>
          <ArrowLeft className={`w-4 h-4 text-gray-400 ${isRTL ? '' : 'rotate-180'}`} />
        </button>

        {/* Admin - Reports (visible only for admins) */}
        {user?.is_admin && (
          <button
            onClick={onOpenAdmin}
            data-testid="settings-admin-reports"
            className="w-full bg-white rounded-xl border border-purple-200 px-4 py-3.5 flex items-center justify-between hover:bg-purple-50"
          >
            <span className="flex items-center gap-3 text-sm font-semibold text-gray-800">
              <ShieldAlert className="w-4 h-4 text-purple-600" />
              {t('adminReports')}
            </span>
            <ArrowLeft className={`w-4 h-4 text-gray-400 ${isRTL ? '' : 'rotate-180'}`} />
          </button>
        )}

        {/* Logout */}
        <button
          onClick={onLogout}
          data-testid="settings-logout"
          className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-red-200 text-red-600 font-semibold hover:bg-red-50"
        >
          <LogOut className="w-4 h-4" />
          {t('logout')}
        </button>
      </div>
    </div>
  );
};

export default ProfileSettings;
